import {ServiceAccountTypeName} from "app/services/domain/service-accounts/service-accounts";
import * as Models from "app/services/proxy/model/models";
import {ClusterType, RESOURCE_SERVICE_LABEL_MAP, ServiceProviderName} from "app/services/ui/platform";

import {PluralizedValues, UtilsService} from "framework/services/utils.service";

const CLOUD_LOGO_PATH               = "assets/icons/cloud-logo";
export const AWS_DEFAULT_SVG_PATH   = `${CLOUD_LOGO_PATH}/AWS-logo.svg`;
export const AZURE_DEFAULT_SVG_PATH = `${CLOUD_LOGO_PATH}/Azure-logo.svg`;
export const GCP_DEFAULT_SVG_PATH   = `${CLOUD_LOGO_PATH}/GCP-logo.svg`;
export const OCI_DEFAULT_SVG_PATH   = `${CLOUD_LOGO_PATH}/Oracle-logo.svg`;

const SERVICE_PROVIDERS_ICON_PATH     = "assets/icons/service-providers";
const AWS_SERVICE_PROVIDER_SVG_PATH   = `${SERVICE_PROVIDERS_ICON_PATH}/AWS.svg`;
const AZURE_SERVICE_PROVIDER_SVG_PATH = `${SERVICE_PROVIDERS_ICON_PATH}/Azure.svg`;
const GCP_SERVICE_PROVIDER_SVG_PATH   = `${SERVICE_PROVIDERS_ICON_PATH}/GCP.svg`;
const OCI_SERVICE_PROVIDER_SVG_PATH   = `${SERVICE_PROVIDERS_ICON_PATH}/Oracle.svg`;

export class CloudProvidersService
{
    public static readonly ALL_CLOUDS: Models.CloudServiceName[] = UtilsService.getEnumValues(Models.CloudServiceName);

    public static readonly jiraFriendlyLabel: string      = "Jira Service Management";
    public static readonly saasCustomToolFriendly: string = "Custom SaaS Tool";
    public static readonly ibmTencentFriendly: string     = "IBM (Tencent)";

    public static getImageURL(value: string)
    {
        // Construct the full URL using base href and origin
        const baseHref = document.querySelector("base")
                                 ?.getAttribute("href") || "/";
        return `${document.location.origin}${baseHref}${value}`;
    }

    public static isCloudProvider(cloud: string): cloud is CloudProvider
    {
        switch (cloud)
        {
            case Models.CloudServiceName.AWS:
            case Models.CloudServiceName.GCP:
            case Models.CloudServiceName.OCI:
                return true;
        }

        if (this.isAzure(<Models.CloudServiceName>cloud)) return true;
        return false;
    }

    public static getServiceProviderSvg(id: Models.CloudServiceName | Models.ResourceName | Models.SaasServiceName): string
    {
        switch (id)
        {
            case Models.CloudServiceName.AWS:
                return AWS_SERVICE_PROVIDER_SVG_PATH;

            case Models.CloudServiceName.GCP:
                return GCP_SERVICE_PROVIDER_SVG_PATH;

            case Models.CloudServiceName.OCI:
                return OCI_SERVICE_PROVIDER_SVG_PATH;
        }

        if (this.isAzure(id as Models.CloudServiceName))
        {
            return AZURE_SERVICE_PROVIDER_SVG_PATH;
        }

        switch (id)
        {
            case Models.SaasServiceName.OpenAI:
                return "assets/icons/saas-services/chatGPT-logo.svg";

            case Models.ResourceName.Anthropic:
                return "assets/icons/saas-services/anthropic-logo.svg";

            case Models.SaasServiceName.Snowflake:
                return "assets/icons/saas-services/snowFlake-logo.svg";

            case Models.SaasServiceName.Databricks:
                return "assets/icons/saas-services/dataBricks-logo.svg";

            case Models.SaasServiceName.Datadog:
                return "assets/icons/saas-services/dataDog-logo.svg";

            case Models.SaasServiceName.Microsoft_Teams:
                return "assets/icons/saas-services/microsoftTeams-logo.svg";

            case Models.SaasServiceName.Salesforce_Sales_Cloud:
                return "assets/icons/saas-services/salesforce-logo.svg";

            case Models.SaasServiceName.Dropbox:
                return "assets/icons/saas-services/dropbox-logo.svg";

            case Models.SaasServiceName.Okta:
                return "assets/icons/saas-services/okta-logo.svg";

            case Models.SaasServiceName.New_Relic:
                return "assets/icons/saas-services/newRelic-logo.svg";

            case Models.SaasServiceName.Dynatrace:
                return "assets/icons/saas-services/dynatrace-logo.svg";

            case Models.SaasServiceName.Grafana:
                return "assets/icons/saas-services/grafana-logo.svg";

            case Models.SaasServiceName.Elastic:
                return "assets/icons/saas-services/elastic-logo.svg";

            case Models.SaasServiceName.Splunk:
                return "assets/icons/saas-services/splunk-logo.svg";

            case Models.SaasServiceName.IBM_Tencent:
                return "assets/icons/saas-services/tencent-logo.svg";

            default:
                return "assets/icons/other/account-type/Cube.svg";
        }
    }

    public static getServiceProviderIcon(id: Models.CloudServiceName): string
    {
        switch (id)
        {
            case Models.CloudServiceName.AWS:
                return "cs-icon-aws";

            case Models.CloudServiceName.GCP:
                return "cs-icon-gcp";

            case Models.CloudServiceName.OCI:
                return "cs-icon-oci";

            default:
                return this.isAzure(id) ? "cs-icon-azure" : null;
        }
    }

    public static getServiceProviderPluralizedRegion(...clouds: Models.CloudServiceName[]): PluralizedValues
    {
        let allAzure = clouds.every(cloud => this.isAzure(cloud));

        if (clouds.length && allAzure)
        {
            return new PluralizedValues("Locations", "Location", "Locations");
        }
        else
        {
            return new PluralizedValues("Regions", "Region", "Regions");
        }
    }

    public static getServiceProviderPluralizedCloudAccount(...clouds: Models.CloudServiceName[]): PluralizedValues
    {
        let allAzure = clouds.every(cloud => this.isAzure(cloud));

        if (clouds.length && allAzure)
        {
            return new PluralizedValues("Subscriptions", "Subscription", "Subscriptions");
        }
        else
        {
            return new PluralizedValues("Cloud Accounts", "Cloud Account", "Cloud Accounts");
        }
    }

    public static isAzure(cloud: Models.CloudServiceName): boolean
    {
        switch (cloud)
        {
            case Models.CloudServiceName.Azure:
            case Models.CloudServiceName.AzureStack:
            case Models.CloudServiceName.Azure_CSP:
            case Models.CloudServiceName.Azure_CSP_Direct:
            case Models.CloudServiceName.Azure_EA:
            case Models.CloudServiceName.Azure_MCA:
                return true;
        }
        return false;
    }

    private static readonly clusterFriendlyNames: Partial<Record<ClusterType, string>> = {
        [ClusterType.Amazon_EKS]: "Amazon EKS",
        [ClusterType.Azure_AKS] : "Azure AKS",
        [ClusterType.GCP_GKE]   : "Google GKE"
    };

    public static getCloudFriendly(cloud: ServiceAccountTypeName | Models.SaasServiceName): string
    {
        const clusterName = CloudProvidersService.clusterFriendlyNames[cloud as ClusterType];
        if (clusterName) return clusterName;

        const integratedTool = UtilsService.parseEnumValue(Models.IntegratedToolName, cloud);
        if (integratedTool === Models.IntegratedToolName.JIRA_ServiceManagement) return CloudProvidersService.jiraFriendlyLabel;

        const resourceName  = UtilsService.parseEnumValue<Models.ResourceName>(Models.ResourceName, cloud);
        const resourceLabel = resourceName ? RESOURCE_SERVICE_LABEL_MAP[resourceName] : null;
        if (resourceLabel) return resourceLabel;

        const cloudServiceName = UtilsService.parseEnumValue(Models.CloudServiceName, cloud);
        const saasServiceName  = UtilsService.parseEnumValue(Models.SaasServiceName, cloud);
        if (saasServiceName === Models.SaasServiceName.IBM_Tencent) return CloudProvidersService.ibmTencentFriendly;
        if (saasServiceName === Models.SaasServiceName.SaasCustomTool) return CloudProvidersService.saasCustomToolFriendly;

        if (!cloudServiceName && !integratedTool && !saasServiceName) return "";

        return cloud.replace(/_/g, " ");
    }

    static getPolicyCloudProviderSvg(cloud: Models.PolicyServiceName): string
    {
        switch (cloud)
        {
            case Models.PolicyServiceName.AWS:
                return AWS_SERVICE_PROVIDER_SVG_PATH;

            case Models.PolicyServiceName.Azure:
            case Models.PolicyServiceName.AzureStack:
            case Models.PolicyServiceName.Azure_EA:
                return AZURE_SERVICE_PROVIDER_SVG_PATH;

            case Models.PolicyServiceName.GCP:
                return GCP_SERVICE_PROVIDER_SVG_PATH;

            case Models.PolicyServiceName.OCI:
                return OCI_SERVICE_PROVIDER_SVG_PATH;
        }

        return null;
    }

    static getPolicyCloudProviderByCloud(clouds: Models.CloudServiceName[]): Models.PolicyServiceName[]
    {
        return clouds.map(cloud => this.getPolicyProvider(cloud));
    }

    static getPolicyProvider(cloud: Models.CloudServiceName)
    {
        switch (cloud)
        {
            case Models.CloudServiceName.AWS:
                return Models.PolicyServiceName.AWS;

            case Models.CloudServiceName.Azure:
                return Models.PolicyServiceName.Azure;

            case Models.CloudServiceName.AzureStack:
                return Models.PolicyServiceName.AzureStack;

            case Models.CloudServiceName.Azure_EA:
                return Models.PolicyServiceName.Azure_EA;

            case Models.CloudServiceName.GCP:
                return Models.PolicyServiceName.GCP;

            case Models.CloudServiceName.OCI:
                return Models.PolicyServiceName.OCI;
        }
        return null;
    }

    public static toCloudProvider(cloud: Models.CloudServiceName): Models.CloudServiceName
    {
        if (this.isAzure(cloud)) return Models.CloudServiceName.Azure;

        switch (cloud)
        {
            case Models.CloudServiceName.AWS:
            case Models.CloudServiceName.GCP:
            case Models.CloudServiceName.OCI:
                return cloud;

            default:
                return null;
        }
    }

    public static toSaasProvider(saas: ServiceProviderName): ServiceProviderName
    {
        switch (saas)
        {
            case Models.SaasServiceName.OpenAI:
            case Models.SaasServiceName.Snowflake:
            case Models.SaasServiceName.Databricks:
            case Models.SaasServiceName.Datadog:
            case Models.SaasServiceName.Microsoft_Teams:
            case Models.SaasServiceName.Salesforce_Sales_Cloud:
            case Models.SaasServiceName.Dropbox:
            case Models.SaasServiceName.Okta:
            case Models.SaasServiceName.New_Relic:
            case Models.SaasServiceName.Dynatrace:
            case Models.SaasServiceName.Grafana:
            case Models.SaasServiceName.Elastic:
            case Models.SaasServiceName.Splunk:
            case Models.SaasServiceName.CircleCI:
            case Models.SaasServiceName.IBM_Tencent:
            case Models.SaasServiceName.SaasCustomTool:
                return saas;

            default:
                return null;
        }
    }
}

export enum CloudProvider
{
    AWS   = Models.CloudServiceName.AWS,
    Azure = Models.CloudServiceName.Azure,
    GCP   = Models.CloudServiceName.GCP,
    OCI   = Models.CloudServiceName.OCI
}
